/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthContext } from 'app/um/lib/auth-context';
import { DateComparison } from 'app/shared/lib/date-utilities';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthPortalPageContext extends AuthContext {

    ////****************** Fields and Properties **************************************////

    public timestamp: Date | null = null;
    // For the update modal request, these arrays will be populated with the ids of the existing details that are missing required fields and should be treated as newly inserted to allow the user to finish the details.
    public missingFieldExistingServiceIds: string[] = [];
    public missingFieldExistingMedicationIds: string[] = [];

    ////****************** Lifecycle ***********************************************////

    ////****************** Functions ***********************************************////
    public async refreshAuthSettings(auth?: UM.Auth): Promise<void> {
        await super.refreshAuthSettings(auth);
    }

    public async authDataComplete(authId: string): Promise<boolean> {
        this.authRequiredForDataEntryMet = this.checkRequiredForDataEntry(this.auth).length === 0;
        return Promise.resolve((this.authRequiredForDataEntryMet && !!this.auth?.status?.flag?.isInitial));
    }

    protected checkRequiredForDataEntry(auth?: UM.Auth): string[] {
        this.missingFieldExistingMedicationIds = [];
        this.missingFieldExistingServiceIds = [];
        const missingFields: string[] = [];

        if(!this.isConfigured) {
            throw new Error('Auth context not configured');
        }
        if(!auth) {
            auth = this.auth;
        }

        if(!auth) {
            throw new Error('missing auth object');
        }

        // separate out services from dental services
        const services: UM.AuthService[] = auth?.services?.filter(service => this.filterServicesOnDental(service, false)) || [];
        const dentalServices: UM.AuthService[] = auth?.services?.filter(service => this.filterServicesOnDental(service, true)) || [];

        if(this.settings.services.reqDataEntry) {
            this.settings.services.reqNotMet = false;
            if(services.length === 0) {
                this.settings.services.reqNotMet = true;
                missingFields.push('um:service.$_plural');
            }
        }

        if(this.settings.servicesAuthDate.reqDataEntry || this.settings.servicesExpDate.reqDataEntry) {
            let missingField: boolean = false;
            this.settings.servicesAuthDate.reqNotMet = false;

            services.forEach(service => {
                let serviceMissingField: boolean = false;

                if(!service.definition?.id) { serviceMissingField = true; }

                //make sure each field that is required is filled in
                if(!service.authorizedDate && this.settings.servicesAuthDate.reqDataEntry) { serviceMissingField = true; }
                if(!service.expirationDate && this.settings.servicesExpDate.reqDataEntry) { serviceMissingField = true; }

                //validate that the dates selected aren't outside the service valid dates
                if(!DateComparison.checkForContainingDates(service.definition?.dateRange?.start, service.definition?.dateRange?.end,
                    service.authorizedDate, service.expirationDate)) {
                    serviceMissingField = true;
                }

                if(serviceMissingField) {
                    if(!missingField) {
                        missingField = true;
                    }
                    if(!!service.id && !this.missingFieldExistingServiceIds.find(serviceId => serviceId === service.id)) {
                        this.missingFieldExistingServiceIds.push(service.id);
                    }
                }
            });

            if(missingField) {
                this.settings.servicesAuthDate.reqNotMet = true;
                missingFields.push('um:service.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.servicesDental.reqDataEntry) {
            this.settings.servicesDental.reqNotMet = false;
            if(dentalServices.length === 0) {
                this.settings.servicesDental.reqNotMet = true;
                missingFields.push('um:service.dental.$long_plural');
            }
        }

        if(this.settings.servicesDentalAuthDate.reqDataEntry || this.settings.servicesDentalExpDate.reqDataEntry) {
            let missingField: boolean = false;
            this.settings.servicesDentalAuthDate.reqNotMet = false;

            dentalServices.forEach(service => {
                let serviceMissingField: boolean = false;

                if(!service.definition?.id) { serviceMissingField = true; }

                //make sure each field that is required is filled in
                if(!service.authorizedDate && this.settings.servicesDentalAuthDate.reqDataEntry) { serviceMissingField = true; }
                if(!service.expirationDate && this.settings.servicesDentalExpDate.reqDataEntry) { serviceMissingField = true; }

                //validate that the dates selected aren't outside the service valid dates
                if(!DateComparison.checkForContainingDates(service.definition?.dateRange?.start, service.definition?.dateRange?.end,
                    service.authorizedDate, service.expirationDate)) {
                    serviceMissingField = true;
                }

                if(serviceMissingField) {
                    if(!missingField) {
                        missingField = true;
                    }
                    if(!!service.id && !this.missingFieldExistingServiceIds.find(serviceId => serviceId === service.id)) {
                        this.missingFieldExistingServiceIds.push(service.id);
                    }
                }
            });

            if(missingField) {
                this.settings.servicesDentalAuthDate.reqNotMet = true;
                missingFields.push('um:service.dental.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.chiefComplaint.reqDataEntry) {
            this.settings.chiefComplaint.reqNotMet = false;
            if(!auth.chiefComplaint) {
                this.settings.chiefComplaint.reqNotMet = true;
                missingFields.push('um:auth.chiefComplaint.$');
            }
        }

        if(this.settings.diagnoses.reqDataEntry) {
            this.settings.diagnoses.reqNotMet = false;
            if(!auth.diagnoses || auth.diagnoses.length === 0) {
                this.settings.diagnoses.reqNotMet = true;
                missingFields.push('um:auth.diagnosis.$_plural');
            }
        }

        if(this.settings.drugs.reqDataEntry) {
            this.settings.drugs.reqNotMet = false;
            if(!auth.medications || auth.medications.length === 0) {
                this.settings.drugs.reqNotMet = true;
                missingFields.push('cd:drug.$_plural');
            }
        }

        if(this.settings.drugsAuthDate.reqDataEntry || this.settings.drugsExpDate.reqDataEntry) {
            let missingField: boolean = false;
            this.settings.drugsDate.reqNotMet = false;

            this.auth.medications?.forEach(medication => {
                let medicationMissingField: boolean = false;
                if(!medication.definition?.id) { missingField = true; return; }

                //make sure each field that is required is filled in
                if(!medication.authorizedDate && this.settings.drugsAuthDate.reqDataEntry) { missingField = true; return; }
                if(!medication.expirationDate && this.settings.drugsExpDate.reqDataEntry) { missingField = true; return; }

                //validate that the dates selected aren't outside the drug valid dates
                if(!DateComparison.checkForContainingDates(medication.definition?.dateRange?.start, medication.definition?.dateRange?.end,
                    medication.authorizedDate, medication.expirationDate)) {
                    missingField = true;
                    return;
                }

                if(medicationMissingField) {
                    if(!missingField) {
                        missingField = true;
                    }
                    if(!!medication.id && !this.missingFieldExistingMedicationIds.find(medicationId => medicationId === medication.id)) {
                        this.missingFieldExistingMedicationIds.push(medication.id);
                    }
                }
            });

            if(missingField) {
                this.settings.drugsDate.reqNotMet = true;
                missingFields.push('um:medication.status.date.$_plural');
            }
            // Tim Monh TODO: Add check for overlapping dates if configured to do so. Check history of file to retrieve overlapping date check logic.
        }

        if(this.settings.providerAttending.reqDataEntry) {
            this.settings.providerAttending.reqNotMet = false;
            if(!auth.provider || !auth.provider.attending) {
                this.settings.providerAttending.reqNotMet = true;
                missingFields.push(this.settings.providerAttending.display!);
            }
        }

        if(this.settings.providerRequested.reqDataEntry) {
            this.settings.providerRequested.reqNotMet = false;
            if(!auth.provider || !auth.provider.requested) {
                this.settings.providerRequested.reqNotMet = true;
                missingFields.push(this.settings.providerRequested.display!);
            }
        }

        if(this.settings.providerRequesting.reqDataEntry) {
            this.settings.providerRequesting.reqNotMet = false;
            if(!auth.provider || !auth.provider.requesting) {
                this.settings.providerRequesting.reqNotMet = true;
                missingFields.push(this.settings.providerRequesting.display!);
            }
        }

        if(this.settings.providerServicing.reqDataEntry) {
            this.settings.providerServicing.reqNotMet = false;
            if(!auth.provider || !auth.provider.servicing) {
                this.settings.providerServicing.reqNotMet = true;
                missingFields.push(this.settings.providerServicing.display!);
            }
        }

        if(this.settings.requestedDateTime.reqDataEntry) {
            if(!auth.requestedDateTime) {
                missingFields.push('um:auth.requestedDate.$');
            }
        }

        if(this.settings.serviceRequestedCharges.reqDataEntry) {
            const missingFieldServices = services.filter(s => !s.charge?.requested);
            if(missingFieldServices.length > 0) {
                this.settings.serviceRequestedCharges.reqNotMet = true;
                missingFields.push('um:service.charge.requested.$long');

                const existingServiceIdsToAdd: string[] = missingFieldServices.filter(s => !!s.id && !this.missingFieldExistingServiceIds.includes(s.id)).map(s => s.id!);

                this.missingFieldExistingServiceIds.push(...existingServiceIdsToAdd);
            }
            else {
                this.settings.serviceRequestedCharges.reqNotMet = false;
            }
        }

        if(this.settings.drugRequestedCharges.reqDataEntry) {
            const missingFieldMedications = !!auth.medications ? auth.medications.filter(m => !m.charge?.requested) : [];
            if(missingFieldMedications.length > 0) {
                this.settings.drugRequestedCharges.reqNotMet = true;
                missingFields.push('um:medication.charge.requested.$long');

                const existingMedicationIdsToAdd: string[] = missingFieldMedications.filter(m => !!m.id && !this.missingFieldExistingMedicationIds.includes(m.id)).map(s => s.id!);

                this.missingFieldExistingMedicationIds.push(...existingMedicationIdsToAdd);
            }
            else {
                this.settings.drugRequestedCharges.reqNotMet = false;
            }
        }

        if(this.settings.drugRequestedDays.reqDataEntry) {
            const missingFieldMedications = !!auth.medications ? auth.medications.filter(m => !m.days?.requested) : [];
            if(missingFieldMedications.length > 0) {
                this.settings.drugRequestedDays.reqNotMet = true;
                missingFields.push('um:medication.day.requested.$long_plural');

                const existingMedicationIdsToAdd: string[] = missingFieldMedications.filter(m => !!m.id && !this.missingFieldExistingMedicationIds.includes(m.id)).map(s => s.id!);

                this.missingFieldExistingMedicationIds.push(...existingMedicationIdsToAdd);
            }
            else {
                this.settings.drugRequestedDays.reqNotMet = false;
            }
        }

        if(this.settings.drugRequestedQuantity.reqDataEntry) {
            const missingFieldMedications = !!auth.medications ? auth.medications.filter(m => isNullOrUndefined(m.quantity?.requested?.value) || isNullOrUndefined(m.quantity.requested.unit?.id)) : [];
            if(missingFieldMedications.length > 0) {
                this.settings.drugRequestedQuantity.reqNotMet = true;
                missingFields.push('um:medication.quantity.requested.$long');

                const existingMedicationIdsToAdd: string[] = missingFieldMedications.filter(m => !!m.id && !this.missingFieldExistingMedicationIds.includes(m.id)).map(s => s.id!);

                this.missingFieldExistingMedicationIds.push(...existingMedicationIdsToAdd);
            }
            else {
                this.settings.drugRequestedQuantity.reqNotMet = false;
            }
        }

        this.settings.priority.reqNotMet = false;
        if(this.settings.priority.reqDataEntry) {
            if(!auth.priority) {
                this.settings.priority.reqNotMet = true;
                missingFields.push('um:auth.priority.$');
            }
        }

        this.settings.admissionSource.reqNotMet = false;
        if(this.settings.admissionSource.reqDataEntry) {
            if(!auth.admission?.source) {
                this.settings.admissionSource.reqNotMet = true;
                missingFields.push('um:auth.admission.source.$long');
            }
        }

        this.settings.healthcareServiceType.reqNotMet = false;
        if(this.settings.healthcareServiceType.reqDataEntry) {
            if(!auth.healthcareServiceType) {
                this.settings.healthcareServiceType.reqNotMet = true;
                missingFields.push('um:auth.healthcareServiceType.$');
            }
        }

        this.settings.placeOfService.reqNotMet = false;
        if(this.settings.placeOfService.reqDataEntry) {
            if(!auth.placeOfService) {
                this.settings.placeOfService.reqNotMet = true;
                missingFields.push('cd:auth.placeOfService.$');
            }
        }

        return missingFields;

    }

    protected checkRequiredForDetermination(auth?: UM.Auth): string[] {
        return [];
    }

    protected filterServicesOnDental = (service: UM.AuthService, dentalOnly: boolean): boolean => {
        return dentalOnly ? service.definition?.type?.bodySiteType?.code === 'Dental' : service.definition?.type?.bodySiteType?.code !== 'Dental';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAuth(authId: string): Promise<UM.Auth> {
        const memberIncludes: string[] = [
            'member',                               // these for the member-plan-select component on create-auth-step
            'member.address.home',
            'member.gender',
            'member.plan',
            'member.plan.eligibility',              // these for cwqi
            'member.plan.eligibility.product',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'member.plan.eligibility.history',              // these for cwqi
            'member.plan.eligibility.history.product',
            'member.gender',
            'member.primaryCarePhysician',
            'member.primaryCarePhysician.location', // these for the readonly provider-location-select on auth-details-step
            'member.primaryCarePhysician.location.facility',
            'member.primaryCarePhysician.location.facility.address.physical',
            'member.primaryCarePhysician.location.provider'
        ];

        const providerRequestedIncludes: string[] = [
            'provider.requested',                   // this for the auth-provider components on auth-details-step
            'provider.requested.location',          // these for the fax cover sheet
            'provider.requested.location.facility',
            'provider.requested.location.facility.address.physical',
            'provider.requested.location.facility.phoneNumber.fax',
            'provider.requested.location.facility.phoneNumber.primary',
            'provider.requested.location.facility.phoneNumber.secondary',
            'provider.requested.location.provider'
        ];

        const providerRequestingIncludes: string[] = [
            'provider.requesting',                  // this for the auth-provider components on auth-details-step
            'provider.requesting.location',         // these for the fax cover sheet
            'provider.requesting.location.facility',
            'provider.requesting.location.facility.address.physical',
            'provider.requesting.location.facility.phoneNumber.fax',
            'provider.requesting.location.facility.phoneNumber.primary',
            'provider.requesting.location.facility.phoneNumber.secondary',
            'provider.requesting.location.provider'
        ];

        const providerServicingIncludes: string[] = [
            'provider.servicing',                   // this for the auth-provider components on auth-details-step
            'provider.servicing.location',          // these for the fax cover sheet
            'provider.servicing.location.facility',
            'provider.servicing.location.facility.address.physical',
            'provider.servicing.location.facility.phoneNumber.fax',
            'provider.servicing.location.facility.phoneNumber.primary',
            'provider.servicing.location.facility.phoneNumber.secondary',
            'provider.servicing.location.provider'
        ];

        const providerAttendingIncludes: string[] = [
            'provider.attending',                   // this for the auth-provider components on auth-details-step
            'provider.attending.location',          // these for the fax cover sheet
            'provider.attending.location.facility',
            'provider.attending.location.facility.address.physical',
            'provider.attending.location.facility.phoneNumber.fax',
            'provider.attending.location.facility.phoneNumber.primary',
            'provider.attending.location.facility.phoneNumber.secondary',
            'provider.attending.location.provider'
        ];

        const authIncludes: string[] = [
            'assessments',
            'assessments.attachment',
            'chiefComplaint',
            'class',
            'class.subClass',
            'class.subClass.assessments',           // these for the assessments-step
            'diagnoses',                            // this for the diagnosis-grid required-for-entry check
            'diagnoses.definition',
            'diagnoses.definition.type.mappings',
            'diagnoses.flags',
            'diagnoses.medicalReview',              // these for the iqc-document-step
            'externalSystem',                       // this to differentiate externalSystem by code on auto-auth-step and various auth detail grids
            'latestEagleTransaction',               // this for the submit auth confirmation step
            'medications',                          // this for the medications-grid required-for-entry check
            'medications.definition',
            'medications.status',
            'medications.status.reason',
            'healthcareServiceType',
            'placeOfService',
            'preferredContact',                     // for existing-contact within auto-auth-step
            'priority',                             // for auth-information display
            'providers',                            // this to check whether or not a selected provider has flag.isContracted on auth-details-step
            'providers.detail.type',                // these are for MCG CWQI\AutoAuth
            'services',                             // this for the service-grid required-for-entry check
            'services.definition',                  // this for the submit auth cwqi and iqc step
            'services.definition.type',             // this for the submit auth cwqi and iqc step
            'services.definition.type.bodySiteType',// this to filter services from dental services by code on auth-details-step
            'services.definition.type.mappings',
            'services.definition.type.mappings.externalSystem',
            'services.flags',
            'services.medicalReview',               // these for the iqc-document-step
            'services.status',                      // this is for the auto-auth-step
            'services.status.determination.status',
            'status',                               // this for the confirmation-step
            'type',
            'validation.results'
        ];

        try {
            const [auth, memberAuth, providerRequestedAuth, providerRequestingAuth, providerServicingAuth, providerAttendingAuth] = await Promise.all([
                this.authService.loadAuth(authId, authIncludes),
                this.authService.loadAuth(authId, memberIncludes),
                this.authService.loadAuth(authId, providerRequestedIncludes),
                this.authService.loadAuth(authId, providerRequestingIncludes),
                this.authService.loadAuth(authId, providerServicingIncludes),
                this.authService.loadAuth(authId, providerAttendingIncludes)
            ]);

            const returnedAuth: UM.Auth = auth.results[0];

            const latestEagleTransaction = returnedAuth.latestEagleTransaction;
            if(latestEagleTransaction) this.latestEagleTransaction = latestEagleTransaction;

            // stitch together the various API calls
            returnedAuth.member = memberAuth.results[0].member;
            if(!returnedAuth.provider) returnedAuth.provider = {};
            returnedAuth.provider.requested = providerRequestedAuth.results[0].provider?.requested;
            returnedAuth.provider.requesting = providerRequestingAuth.results[0].provider?.requesting;
            returnedAuth.provider.servicing = providerServicingAuth.results[0].provider?.servicing;
            returnedAuth.provider.attending = providerAttendingAuth.results[0].provider?.attending;

            return returnedAuth;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    @apiCall
    private async dataEntryComplete(authId: string): Promise<void> {
        try {
            const response = await this.authService.completeEntry({ id: authId });

            if(response.isSuccessful) {
                this.toaster.info().message(this.i18n.tr('ui:messages.events.updated', { what: this.i18n.tr('um:auth.status.$') })).show();
            }
            else {
                this.toaster.error().message('Unable to move into data entry complete status').show();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }
}
